import React, { useState, useContext, useEffect } from "react";
import { Row, Col, Input, Badge, Button, Popover, Drawer, Typography, Divider, Switch, Avatar, AutoComplete } from "antd";
import {
  IconPackage,
  IconTag,
  IconSun,
  IconMoon,
  IconFilter,
  IconSearch,
  IconUser,
  IconChevronDown,
  IconHeart,
  IconMenu2,
  IconRocket,
  IconMessage,
  IconWallet,
  IconAddressBook,
  IconLayoutDashboard,
  IconShoppingBag,
  IconMapPin,
  IconAdjustmentsHorizontal,
  IconLayoutGrid,
  IconShoppingCart,
  IconBell
} from "@tabler/icons-react";
import { CartContext } from "../CartContext";
import { WishlistContext } from "../WishlistContext";
import { LocationContext } from "../LocationContext";
import { jwtDecode } from "jwt-decode";
import AuthServ from "../service/AuthServ";

import { useNavigate, useLocation } from "react-router-dom";
import { ThemeContext } from "../util/ThemeContext";
import io from "socket.io-client";
import ChatServ from "../service/ChatServ";
import NotificationServ from "../service/NotificationServ";
import near from "../assets/img/near.jpg";
import { renderSafeText } from "../util/unitFormatter";
import { getImageUrl } from "../component/SafeImage";
import ChangeLocationContent from "../component/ChangeLocationContent";
import { capitalizeFirst } from "../util/unitFormatter";
import "../css/navbar.css";
import { useGlobalAuthModal } from "../util/GlobalAuthModal";
import Notify from "../util/Notify";
import WishlistDrawer from '../component/WishlistDrawer';
import UserProfileMenu from "../component/UserProfileMenu";
import { usePreference } from "../context/PreferenceContext";
import GlobalTooltip from "../component/GlobalTooltip";
import ProductServ from "../service/ProductServ";
import ShopServ from "../service/ShopServ";
import { useCategory } from "../context/CategoryContext";
const { Text } = Typography;

const Navbar = () => {
  const { t } = usePreference();
  const { cartCount, setIsDrawerOpen } = useContext(CartContext);
  const { wishlistCount } = useContext(WishlistContext);
  const {
    selectedAddress, setSelectedAddress,
    cityName, setCityName,
    latitude, setLatitude,
    longitude, setLongitude,
    radius, setRadius,
    setIsManualLocation
  } = useContext(LocationContext);
  const { darkMode, toggleDarkMode } = useContext(ThemeContext);
  // const [isCartModalOpen, setIsCartModalOpen] = useState(false); // REMOVED
  const [isWishlistModalOpen, setIsWishlistModalOpen] = useState(false);
  const [open, setOpen] = useState(false);
  const [mobileMenuVisible, setMobileMenuVisible] = useState(false);
  const [profileDrawerOpen, setProfileDrawerOpen] = useState(false);
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [mobileSearchVisible, setMobileSearchVisible] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const isAuthPage = ['/login', '/customer-login', '/sellerlogin', '/signup', '/sellersignup', '/customer-register', '/forgot-password', '/start-selling', '/seller-check'].includes(location.pathname);
  const [customerName, setCustomerName] = useState("");
  const [role, setRole] = useState(localStorage.getItem("role") || "");
  const [user, setUser] = useState(null);
  const { showAlert } = useGlobalAuthModal();
  const [, setUnreadChatCount] = useState(0);
  const [searchValue, setSearchValue] = useState("");
  const [credits, setCredits] = useState(0);
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);
  const [searchOptions, setSearchOptions] = useState([]);
  const [hasShops, setHasShops] = useState(true);

  // Consume Global Categories
  const { categories: allCategories, parentCategories } = useCategory();

  useEffect(() => {
    let isCurrent = true;
    const checkShopAvailability = async () => {
      if (latitude && longitude) {
        try {
          const res = await ShopServ.index({ lat: latitude, lng: longitude, radius: radius || 7, limit: 1 });
          if (isCurrent) {
            setHasShops(res?.total > 0 || (res?.data && res.data.length > 0));
          }
        } catch (err) {
          if (isCurrent) setHasShops(true);
        }
      } else {
        if (isCurrent) setHasShops(true);
      }
    };
    
    checkShopAvailability();
    
    // Also listen to manual overrides (e.g. from HomeShopList if it loads before this)
    const handleHasShops = (e) => {
      if (isCurrent) setHasShops(e.detail);
    };
    window.addEventListener('set-has-shops', handleHasShops);
    return () => {
      isCurrent = false;
      window.removeEventListener('set-has-shops', handleHasShops);
    };
  }, [latitude, longitude, radius]);

  const searchTimeoutRef = React.useRef(null);

  // Helper: perform search on search results screen
  const handlePerformSearch = (query) => {
    const q = (query || '').trim();
    if (!q) return;
    navigate(`/search?search=${encodeURIComponent(q)}`);
  };

  const handleSearchSuggestions = (value) => {
    setSearchValue(value);

    if (!value || !value.trim()) {
      setSearchOptions([]);
      return;
    }

    const lowerValue = value.toLowerCase().trim();

    // Always provide an option to search the exact term
    let options = [
      {
        value: value,
        searchQuery: value,
        isCategory: false,
        label: (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <IconSearch size={16} style={{ marginRight: 8, color: 'var(--color-text-muted)' }} />
            <span>Search for <b>"{value}"</b></span>
          </div>
        )
      }
    ];

    // Find matching categories
    const matchingCategories = allCategories
      .filter(cat => cat.name && cat.name.toLowerCase().includes(lowerValue))
      .slice(0, 4); // Limit to 4 category suggestions

    matchingCategories.forEach(cat => {
      options.push({
        value: `${value} in ${cat.name}`,
        searchQuery: value,
        categoryName: cat.name,
        isCategory: true,
        label: (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <IconSearch size={16} style={{ marginRight: 8, color: 'var(--color-text-muted)' }} />
            <span>
              {value} in <b style={{ color: 'var(--nearzon-orange)' }}>{cat.name}</b>
            </span>
          </div>
        )
      });
    });

    setSearchOptions(options);

    // Fetch product suggestions
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (value.length >= 2) {
      searchTimeoutRef.current = setTimeout(async () => {
        try {
          const res = await ProductServ.search(value, { limit: 5 });
          const products = res.data?.products || res.data || [];
          
          if (products && products.length > 0) {
            const productOptions = products.slice(0, 5).map(prod => ({
              value: prod.name,
              searchQuery: prod.name,
              isProduct: true,
              productId: prod._id || prod.id,
              label: (
                <div style={{ display: 'flex', alignItems: 'center', maxWidth: '100%' }}>
                  <IconPackage size={16} style={{ marginRight: 8, color: 'var(--color-text-muted)', flexShrink: 0 }} />
                  <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{prod.name}</span>
                </div>
              )
            }));
            
            setSearchOptions(prevOptions => {
              // Ensure we only append if the user hasn't typed something completely different
              if (prevOptions.length > 0 && prevOptions[0].searchQuery === value) {
                return [...prevOptions, ...productOptions];
              }
              return prevOptions;
            });
          }
        } catch (err) {
          console.error("Product suggestion fetch failed", err);
        }
      }, 300);
    }
  };

  // Sync Input FROM URL (Handling Back/Forward or direct link)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const q = params.get("search") || "";
    // Only update search value if we are actually on a page that uses it
    if (location.pathname === "/search" || location.pathname === "/") {
      if (q !== searchValue) {
        setSearchValue(q);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, location.pathname]);



  const handleLogout = () => {
    showAlert({
      title: t('logout_title'),
      description: t('logout_description'),
      buttons: [
        { text: t('stay_here'), variant: 'default' },
        {
          text: t('sign_out'),
          variant: 'primary',
          onClick: () => {
            // Remove all auth data
            localStorage.removeItem("token");
            localStorage.removeItem("role");
            localStorage.removeItem("user");
            localStorage.removeItem("customerName");

            // Legacy/Customer specific
            localStorage.removeItem("customerToken");
            localStorage.removeItem("customerId");

            setCustomerName("");
            setRole("");
            setUser(null);
            window.dispatchEvent(new Event("auth-change"));
            window.location.href = "/";
          }
        }
      ]
    });
  };


  useEffect(() => {
    const token = localStorage.getItem("token") || localStorage.getItem("customerToken");

    if (token) {
      // 1. Initial name from localStorage if exists
      const savedName = localStorage.getItem("customerName");
      if (savedName) setCustomerName(savedName);

      // 2. Sync Profile from Backend (Source of Truth)
      AuthServ.single().then(res => {
        if (res?.data) {
          const userObj = res.data;
          setUser(userObj); // Set full user object for profile photo etc.
          const latestRole = userObj.role?.toLowerCase();
          setRole(latestRole);
          localStorage.setItem("role", latestRole);

          let displayName = "";
          if (latestRole === "superadmin") {
            displayName = "Super Admin";
          } else if (latestRole === "admin") {
            displayName = "Admin";
          } else {
            displayName = userObj.firstName
              ? `${userObj.firstName} ${userObj.lastName || ""}`.trim()
              : userObj.name || userObj.email?.split('@')[0];
          }

          // Capitalize standard names
          if (displayName && !["Super Admin", "Admin"].includes(displayName)) {
            displayName = displayName.split(' ')
              .map(s => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase())
              .join(' ');
          }

          setCustomerName(displayName || "User");
          localStorage.setItem("customerName", displayName);
          setCredits(userObj.credits || 0);

          // Auto redirect if seller
          if (latestRole === "seller" && window.location.pathname === "/start-selling") {
            navigate("/seller/dashboard");
          }
        }
      }).catch(err => console.error("Profile sync failed", err));

      try {
        const decoded = jwtDecode(token);
        setUser(decoded);
      } catch (e) { }

      // Fetch initial unread count
      refreshUnreadCount();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refreshUnreadCount = () => {
    ChatServ.getUnreadCount().then(res => {
      setUnreadChatCount(res.data || 0);
    }).catch(err => console.error("Unread count fetch failed", err));

    NotificationServ.getUnreadCount(role).then(res => {
      setUnreadNotificationCount(res.data || 0);
    }).catch(err => console.error("Notification count fetch failed", err));
  };

  useEffect(() => {
    const handleCreditsRefresh = () => {
      refreshUnreadCount();
      AuthServ.single().then(res => {
        if (res?.data) {
          setCredits(res.data.credits || 0);
        }
      });
    };

    window.addEventListener("chat-refresh", refreshUnreadCount);
    window.addEventListener("notification-refresh", refreshUnreadCount);
    window.addEventListener("credits-refresh", handleCreditsRefresh);
    
    return () => {
      window.removeEventListener("chat-refresh", refreshUnreadCount);
      window.removeEventListener("notification-refresh", refreshUnreadCount);
      window.removeEventListener("credits-refresh", handleCreditsRefresh);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role]);

  // 💬 Real-time Chat Notifications
  useEffect(() => {
    const token = localStorage.getItem("token") || localStorage.getItem("customerToken");
    if (!token || !user) return;

    const socketUrl = 'http://localhost:4000';
    const socket = io(socketUrl);

    socket.on('connect', () => {
      socket.emit('join', user.id || user._id);
    });

    socket.on('newMessageNotification', (data) => {
      // Don't show notification if we are already on the chat page or have it open?
      // For now, always show a notification unless we are on the /messages route maybe?

      setUnreadChatCount(prev => prev + 1);

      Notify.info('New Message', data.message.text.length > 50
        ? data.message.text.substring(0, 50) + "..."
        : data.message.text);
    });

    return () => {
      socket.disconnect();
    };
  }, [user, role]);


  const handleCustomerLogin = () => {
    navigate("/customer-login");
    setMobileMenuVisible(false);
  };
  const handleCustomerRegister = () => {
    navigate("/customer-register");
    setMobileMenuVisible(false);
  };

  // Updated to handle both guest and logged-in users
  const handleSellerLogin = () => {
    // If logged in, go to instant request flow
    if (AuthServ.isLogged()) {
      handleInstantSellerRequest();
    } else {
      navigate("/start-selling");
    }
  };

  // 🔹 Transition to Seller Onboarding Form
  const handleInstantSellerRequest = () => {
    navigate("/sellerlogin");
    setMobileMenuVisible(false); // Close mobile drawer if open
  };


  // 🔹 Apply selected location
  const handleApply = (data) => {
    let addressText = "";
    let cityText = "";
    if (typeof data === "string") addressText = data;
    else if (typeof data === "object") {
      addressText =
        data.address ||
        data.place_name ||
        data.formatted_address ||
        data.name ||
        data.label ||
        JSON.stringify(data);

      cityText = data.city || "";

      // Update Context
      if (data.lat && data.lng) {
        setLatitude(data.lat);
        setLongitude(data.lng);
        setIsManualLocation(true);
      }
      if (data.radius) {
        setRadius(data.radius);
      }
    }

    setSelectedAddress(addressText);
    setCityName(cityText);
    window.__isMapSearching = false; // Clear flag to allow closing
    setOpen(false);
    setLocationModalOpen(false);
    navigate("/");
  };

  // 🔹 Reset selected location
  const handleReset = () => {
    setSelectedAddress("");
    setCityName("");
    setLatitude(null);
    setLongitude(null);
    setRadius(7);
    setIsManualLocation(false);
    window.__isMapSearching = false; // Clear flag to allow closing
    setOpen(false);
    setLocationModalOpen(false);
  };

  // No longer need separate load-from-localStorage since context handles sessionStorage
  const handleProfileNavigate = () => {
    const r = role?.toLowerCase();
    if (r === "user" || r === "buyer" || r === "" || !r) {
      navigate("/buyer/profile");
    } else if (r === "seller") {
      navigate("/seller/profile");
    } else if (r === "admin" || r === "superadmin") {
      navigate("/admin/profile");
    }
    setMobileMenuVisible(false);
  };






  const mobileMenuContent = (
    <div className="mobile-menu-wrapper">
      {customerName ? (
        <div className="mobile-menu-header">
          <div className="mobile-menu-header-top">
            <img src={near} alt="logo" className="mobile-logo-mini" onClick={() => { navigate("/"); setMobileMenuVisible(false); }} />
            <Button type="text" icon={<IconRocket size={20} className="mobile-close-icon" />} onClick={() => setMobileMenuVisible(false)} />
          </div>
          <div className="mobile-user-profile" onClick={() => { handleProfileNavigate(); setMobileMenuVisible(false); }}>
            <Avatar size={60} icon={<IconUser />} src={getImageUrl(user?.profilePhoto)} className="mobile-avatar" />
            <div className="mobile-user-text">
              <Text strong className="mobile-user-name text-title">{customerName}</Text>
              <div className="mobile-user-info-row">
                <Text type="secondary" className="mobile-user-role text-sub-description">{role?.toUpperCase()}</Text>
                <div className="mobile-user-dot"></div>
                <Text type="secondary" className="mobile-edit-profile-link">{capitalizeFirst(t('edit_profile'))}</Text>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="mobile-auth-section">
          <Button type="primary" block onClick={handleCustomerLogin} className="mobile-auth-btn primary">{capitalizeFirst(t('login'))}</Button>
          <Button block onClick={handleCustomerRegister} className="mobile-auth-btn secondary">{capitalizeFirst(t('signup'))}</Button>
        </div>
      )}

      <Divider style={{ margin: '0' }} />

      <div className="mobile-menu-body">
        {/* Main Navigation Group */}
        <div className="menu-group">
          <Text className="menu-group-title">{capitalizeFirst(t('navigation'))}</Text>
          <div className="menu-item" onClick={() => { navigate("/"); setMobileMenuVisible(false); }}>
            <IconRocket size={20} /> <span>{capitalizeFirst(t('home'))}</span>
          </div>
          <div className="menu-item" onClick={() => { navigate("/all-products"); setMobileMenuVisible(false); }}>
            <IconTag size={20} /> <span>{capitalizeFirst(t('Today_deals'))}</span>
          </div>
          <div className="menu-item" onClick={() => { navigate("/contact"); setMobileMenuVisible(false); }}>
            <IconAddressBook size={20} /> <span>{capitalizeFirst(t('contact_us'))}</span>
          </div>
        </div>

        {/* Account / Dashboard Group */}
        {/* Account / Dashboard Group (Unified for Buyer and Seller) */}
        {(role === 'user' || role === 'buyer' || role === 'seller') && (
          <div className="menu-group">
            <Divider style={{ margin: '15px 0' }} />
            <Text className="menu-group-title">{capitalizeFirst(t('my_account'))}</Text>
            <div className="menu-item" onClick={() => {
              navigate(role === 'seller' ? "/seller/dashboard" : "/buyer/dashboard");
              setMobileMenuVisible(false);
            }}>
              <IconLayoutDashboard size={20} /> <span>{capitalizeFirst(t('dashboard'))}</span>
            </div>
            <div className="menu-item" onClick={() => { navigate("/buyer/orders"); setMobileMenuVisible(false); }}>
              <IconShoppingBag size={20} /> <span>{capitalizeFirst(t('my_orders'))}</span>
            </div>
            <div className="menu-item" onClick={() => { navigate("/buyer/wishlist"); setMobileMenuVisible(false); }}>
              <IconHeart size={20} /> <span>{capitalizeFirst(t('wishlist', 'My Wishlist'))}</span>
            </div>
            <div className="menu-item" onClick={() => { navigate("/buyer/addresses"); setMobileMenuVisible(false); }}>
              <IconMapPin size={20} /> <span>{capitalizeFirst(t('my_addresses'))}</span>
            </div>
            <div className="menu-item" onClick={() => { navigate("/buyer/rewards/credits"); setMobileMenuVisible(false); }}>
              <IconWallet size={20} /> <span>{capitalizeFirst(t('my_wallet'))}</span>
            </div>
            <div className="menu-item" onClick={() => { handleProfileNavigate(); setMobileMenuVisible(false); }}>
              <IconUser size={20} /> <span>{capitalizeFirst(t('profile_settings'))}</span>
            </div>
            {role !== 'seller' && (
              <div className="menu-item" onClick={() => { handleInstantSellerRequest(); setMobileMenuVisible(false); }}>
                <IconRocket size={20} style={{ color: 'var(--nearzon-orange)' }} /> <span style={{ color: 'var(--nearzon-orange)', fontWeight: '600' }}>{capitalizeFirst(t('start_selling'))}</span>
              </div>
            )}
          </div>
        )}

        {role === 'seller' && (
          <div className="menu-group">
            <Divider style={{ margin: '15px 0' }} />
            <Text className="menu-group-title">{capitalizeFirst(t('seller_panel'))}</Text>
            <div className="menu-item" onClick={() => { navigate("/seller/dashboard"); setMobileMenuVisible(false); }}>
              <IconLayoutDashboard size={20} /> <span>{capitalizeFirst(t('seller_panel'))}</span>
            </div>
            <div className="menu-item" onClick={() => { navigate("/seller/messages"); setMobileMenuVisible(false); }}>
              <IconMessage size={20} /> <span>{capitalizeFirst(t('messages'))}</span>
            </div>
            <div className="menu-item" onClick={handleProfileNavigate}>
              <IconUser size={20} /> <span>{capitalizeFirst(t('profile'))}</span>
            </div>
          </div>
        )}

        <Divider style={{ margin: '15px 0' }} />

        {/* Settings / System Group */}
        <div className="menu-group">
          <div className="menu-item appearance-item">
            <div className="appearance-group">
              {darkMode ? <IconMoon size={20} /> : <IconSun size={20} />}
              <span>{capitalizeFirst(t('appearance'))}</span>
            </div>
            <Switch
              size="small"
              checked={darkMode}
              onChange={toggleDarkMode}
              checkedChildren={<IconSun size={12} />}
              unCheckedChildren={<IconMoon size={12} />}
            />
          </div>
          {customerName && (
            <div className="menu-item logout-item" onClick={handleLogout}>
              <IconRocket size={20} className="logout-icon-accent" /> <span>{capitalizeFirst(t('logout'))}</span>
            </div>
          )}
        </div>
      </div>
    </div >
  );

  return (
    <>
      {/* 🔹 Fixed Navbar Wrapper */}
      <div className="navbar-sticky-wrapper">
        {/* 🔹 Top Navbar */}
        <div className="navbar-container">
          <Row align="middle" justify="space-between" style={{ width: '100%', margin: 0 }}>
            <Col>
              <div className="navbar-left">
                <Button
                  type="text"
                  icon={<IconMenu2 size={24} />}
                  className="mobile-burger-btn"
                  onClick={() => setMobileMenuVisible(true)}
                />
                <img
                  src={near}
                  alt="logo"
                  className="navbar-logo"
                  onClick={() => navigate("/")}
                />
                {!isAuthPage && (
                  <div className="desktop-location">
                    <Popover
                      placement="bottomLeft"
                      content={
                        <ChangeLocationContent
                          onApply={handleApply}
                          onReset={handleReset}
                          setSelectedAddress={setSelectedAddress}
                          isOpen={open}
                        />
                      }
                      trigger="click"
                      open={open}
                      onOpenChange={(visible) => {
                        if (window.innerWidth < 768) {
                          setOpen(false);
                          return;
                        }
                        console.log("Location Popover change:", visible);
                        // Prevent closing if we are interacting with search suggestions
                        if (!visible && window.__isMapSearching) return;
                        setOpen(visible);
                      }}
                    >
                      <p
                        className="navbar-location"
                        onClick={() => {
                          if (window.innerWidth < 768) {
                            setLocationModalOpen(true);
                          }
                        }}
                      >
                        {cityName
                          ? cityName
                          : (selectedAddress
                            ? selectedAddress.length > 30
                              ? selectedAddress.slice(0, 30) + "..."
                              : selectedAddress
                            : t('choose_location'))
                        }
                        <IconChevronDown className="navbar-chevron-icon" />
                      </p>
                    </Popover>
                  </div>
                )}
              </div>
            </Col>


            {/* Center: Search */}
            <Col flex="auto" className={`navbar-search-col ${mobileSearchVisible ? 'show-mobile-search' : ''}`} style={{ pointerEvents: 'none', display: isAuthPage ? 'none' : 'block' }}>
              <div style={{ pointerEvents: 'auto' }}>
                <AutoComplete
                  popupMatchSelectWidth={252}
                  style={{ width: '100%' }}
                  options={searchOptions}
                  onSelect={(val, option) => {
                    if (option.isCategory) {
                      navigate(`/search?search=${encodeURIComponent(renderSafeText(option.categoryName))}`);
                    } else if (option.isProduct) {
                      navigate(`/single/Products/${option.productId}`);
                    } else {
                      navigate(`/search?search=${encodeURIComponent(option.searchQuery.trim())}`);
                    }
                  }}
                  onSearch={handleSearchSuggestions}
                  value={searchValue}
                  onChange={(val) => setSearchValue(val)}
                >
                  <Input
                    id="global-search-input"
                    name="search"
                    placeholder={t('search_placeholder')}
                    prefix={<IconSearch size={18} className="navbar-search-icon-prefix" onClick={() => {
                      handlePerformSearch(searchValue);
                    }} />}
                    suffix={
                      ['/shops', '/all-products', '/search'].includes(location.pathname) && (
                        <GlobalTooltip title={t('filters')}>
                          <IconFilter
                            size={18}
                            style={{
                              cursor: 'pointer',
                              color: 'var(--color-text-muted)',
                              marginLeft: '8px'
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              window.dispatchEvent(new CustomEvent('toggle-global-filter'));
                            }}
                          />
                        </GlobalTooltip>
                      )
                    }
                    className="navbar-search-premium"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handlePerformSearch(searchValue);
                      }
                    }}
                  />
                </AutoComplete>
              </div>
            </Col>

            {/* Right: Action Icons (Desktop only or specific mobile icons) */}
            <Col>
              <div className="navbar-right">
                {/* Search Icon (Mobile Only) */}
                <div className="mobile-only-flex">
                  <IconSearch
                    className="navbar-bell-icon"
                    onClick={() => setMobileSearchVisible(!mobileSearchVisible)}
                    style={{ cursor: 'pointer' }}
                  />
                </div>

                {/* Login/Profile Section */}
                {customerName ? (
                  <>
                    {/* Role-based action button */}
                    {role === 'user' && (
                      <Button
                        type="primary"
                        onClick={handleInstantSellerRequest}
                        className="navbar-premium-btn-primary desktop-only-flex"
                      >
                        {t('start_selling')}
                      </Button>
                    )}
                    {role === 'pendingseller' && (
                      <Button
                        type="default"
                        className="navbar-premium-btn-user desktop-only-flex"
                        disabled
                        style={{ opacity: 0.7 }}
                      >
                        {t('under_review')}
                      </Button>
                    )}
                    {role === 'seller' && (
                      <Button
                        type="primary"
                        className="navbar-premium-btn-primary desktop-only-flex"
                        onClick={() => navigate("/seller/dashboard")}
                      >
                        Seller Dashboard
                      </Button>
                    )}
                    <Button
                      type="default"
                      className="navbar-premium-btn-user"
                      onClick={() => setProfileDrawerOpen(true)}
                    >
                      <IconUser size={18} className="navbar-user-icon-mobile" />
                      <span className="desktop-only-inline navbar-user-name-text">{customerName}</span>
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      type="default"
                      onClick={handleCustomerLogin}
                      className="navbar-premium-btn-user"
                    >
                      <span className="desktop-only-inline">{capitalizeFirst(t('user_login'))}</span>
                      <IconUser size={18} className="mobile-only-inline" />
                    </Button>
                    <Button
                      type="primary"
                      onClick={handleSellerLogin}
                      className="navbar-premium-btn-primary desktop-only-flex"
                    >
                      {t('start_selling')}
                    </Button>
                  </>
                )}


                {/* Cart is visible to everyone. Wishlist, Notifications Visible Only to Logged-in Users */}
                <div className="desktop-only-flex">
                  <Badge
                    count={cartCount || 0}
                    showZero
                    className="navbar-cart-badge"
                    style={{ cursor: 'pointer' }}
                  >
                    <IconShoppingCart
                      className="navbar-cart-icon"
                      onClick={() => setIsDrawerOpen(true)}
                    />
                  </Badge>
                </div>
                
                {customerName && (
                  <>
                    <div className="desktop-only-flex">
                      <Badge
                        count={wishlistCount || 0}
                        showZero={false}
                        className="navbar-cart-badge navbar-wishlist-badge"
                        style={{ cursor: 'pointer' }}
                      >
                        <IconHeart
                          className="navbar-heart-icon"
                          onClick={() => setIsWishlistModalOpen(true)}
                        />
                      </Badge>
                    </div>

                    {/* Notification Icon (Now visible on mobile too) */}
                    <div className="">
                      <Badge
                        count={unreadNotificationCount}
                        showZero={false}
                        className="navbar-notification-badge"
                        style={{ cursor: 'pointer' }}
                      >
                        <IconBell
                          className="navbar-bell-icon"
                          onClick={() => {
                            console.log("Notifications clicked");
                            const r = role?.toLowerCase().replace(/\s+/g, '');
                            if (r === 'seller') navigate('/seller/notifications');
                            else if (['admin', 'superadmin'].includes(r)) navigate('/admin/notifications');
                            else navigate('/buyer/notifications');
                          }}
                        />
                      </Badge>
                    </div>
                  </>
                )}
              </div>
            </Col>

          </Row>
        </div>

        {!isAuthPage && (
          <div className="navbar-menu">
            {['/', '/shops', '/all-products', '/search'].includes(location.pathname) && (
              <span
                className="navbar-filter-btn"
                onClick={() => window.dispatchEvent(new CustomEvent('toggle-global-filter'))}
              >
                <IconAdjustmentsHorizontal size={18} />
              </span>
            )}
            <span onClick={() => navigate("/")}>{capitalizeFirst(t('home'))}</span>
            <span onClick={() => navigate("/deals")}>{capitalizeFirst(t('weekly_deals'))}</span>
            {customerName && <span onClick={() => navigate("/buyer/orders")}>{capitalizeFirst(t('my_orders'))}</span>}
            <span onClick={() => navigate("/contact")}>{capitalizeFirst(t('contact_us'))}</span>
            {role === "user" && (
              <span className="navbar-credits navbar-credits-display" onClick={() => navigate("/buyer/profile")}>
                <IconWallet size={18} /> {capitalizeFirst(t('credits'))}: ₹{credits || 0}
              </span>
            )}
          </div>
        )}

        {/* 🔹 Mobile Scroll Menu (Visible < 992px) */}
        {!isAuthPage && (
          <div className="mobile-scroll-menu">
            {['/', '/shops', '/all-products', '/search'].includes(location.pathname) && (
              <span
                className="navbar-filter-btn"
                onClick={() => window.dispatchEvent(new CustomEvent('toggle-global-filter'))}
              >
                <IconAdjustmentsHorizontal size={18} />
              </span>
            )}
            <span onClick={() => navigate("/")}>{capitalizeFirst(t('home'))}</span>
            <span onClick={() => navigate("/deals")}>{capitalizeFirst(t('weekly_deals'))}</span>
            {customerName && <span onClick={() => navigate("/buyer/orders")}>{capitalizeFirst(t('my_orders'))}</span>}
            <span onClick={() => navigate("/contact")}>{capitalizeFirst(t('contact_us'))}</span>
            {role === "user" && (
              <span className="navbar-credits navbar-credits-display" onClick={() => navigate("/buyer/profile")}>
                <IconWallet size={18} /> {capitalizeFirst(t('credits'))}: ₹{credits || 0}
              </span>
            )}
          </div>
        )}

        {/* 🔹 Category Sub-Navigation (Zepto Style) */}
        {!isAuthPage && hasShops && (
          <div className="navbar-categories-container">
            <div className="navbar-categories-scroll">
              <div
                className={`navbar-category-item ${!location.pathname.startsWith('/category') && location.pathname !== '/search' ? 'active' : ''}`}
                onClick={() => navigate("/")}
              >
                <IconLayoutGrid size={20} className="category-icon" />
                <span>{capitalizeFirst(t('All'))}</span>
              </div>

              {parentCategories.map(cat => {
                const currentPath = decodeURIComponent(location.pathname).replace(/\/$/, '').toLowerCase();
                const catPath = `/category/${renderSafeText(cat.name)}`.toLowerCase();
                const isActive = currentPath === catPath;

                return (
                  <div
                    key={cat._id}
                    className={`navbar-category-item ${isActive ? 'active' : ''}`}
                    onClick={() => navigate(`/category/${encodeURIComponent(renderSafeText(cat.name))}`)}
                  >
                    <span>{renderSafeText(cat.name)}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

      </div>

      <Drawer
        placement="left"
        onClose={() => setMobileMenuVisible(false)}
        open={mobileMenuVisible}
        width={300}
        styles={{ body: { padding: 0 } }}
        closable={false}
      >
        {mobileMenuContent}
      </Drawer>
      <WishlistDrawer
        isOpen={isWishlistModalOpen}
        onClose={() => setIsWishlistModalOpen(false)}
      />

      {/* Profile Right Drawer */}
      <Drawer
        placement="right"
        onClose={() => setProfileDrawerOpen(false)}
        open={profileDrawerOpen}
        width={300}
        styles={{ body: { padding: 0 } }}
        closable={false}
      >
        <UserProfileMenu
          role={role}
          handleLogout={handleLogout}
          closeMenu={() => setProfileDrawerOpen(false)}
          userName={customerName}
          userPhoto={user?.profilePhoto}
        />
      </Drawer>

      {/* Mobile Location Drawer */}
      <Drawer
        placement="bottom"
        onClose={() => setLocationModalOpen(false)}
        open={locationModalOpen}
        height="85vh"
        styles={{ body: { padding: 0 } }}
        className="mobile-location-drawer"
        title={t('choose_location')}
      >
        <ChangeLocationContent
          onApply={handleApply}
          onReset={handleReset}
          setSelectedAddress={setSelectedAddress}
          isOpen={locationModalOpen}
          height="350px"
        />
      </Drawer>

    </>
  );
};

export default Navbar;
